import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Collapse,
  Grid,
  Avatar,
  Divider,
  Alert,
} from '@mui/material';
import {
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Undo as UndoIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { format } from 'date-fns';
import { api } from '../services/api-client';

interface AuditLogEntry {
  id: string;
  timestamp: any; // Can be Date, Firestore timestamp, or string
  user: {
    email: string;
    role?: string;
    ip_address?: string;
    user_agent?: string;
  };
  action: string;
  target?: {
    type: string;
    id: string;
    displayName?: string;
  };
  resource?: {
    type: string;
    id: string;
  };
  changes?: {
    before?: any;
    after?: any;
    diff?: any[];
  };
  details?: any;
  environment?: string;
  metadata?: {
    environment?: string;
    changeRequestId?: string;
    rollbackToken?: string;
    rollbackExpiresAt?: any;
  };
  searchTags?: string[];
}
import { useAuth } from '../contexts/GoogleAuthContext';

interface ExpandedRows {
  [key: string]: boolean;
}

export default function AuditLog() {
  const { user } = useAuth();
  const [expandedRows, setExpandedRows] = useState<ExpandedRows>({});
  const [filterUser, setFilterUser] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterTarget, setFilterTarget] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch audit logs
  const { data: auditLogs, isLoading, error } = useQuery(
    ['auditLogs', filterUser, filterAction, filterTarget],
    async () => {
      const logs = await api.getAuditLogs(100);
      return logs as AuditLogEntry[];
    },
    {
      refetchInterval: 30000, // Refresh every 30 seconds
    }
  );

  const handleExpandClick = (auditId: string) => {
    setExpandedRows(prev => ({
      ...prev,
      [auditId]: !prev[auditId]
    }));
  };

  const getActionIcon = (action: string) => {
    if (action.includes('grant')) return <CheckCircleIcon color="success" fontSize="small" />;
    if (action.includes('revoke')) return <CancelIcon color="error" fontSize="small" />;
    if (action.includes('rollback')) return <UndoIcon color="warning" fontSize="small" />;
    if (action.includes('security')) return <SecurityIcon color="error" fontSize="small" />;
    return <HistoryIcon color="action" fontSize="small" />;
  };

  const getActionLabel = (action: string) => {
    const labels: { [key: string]: string } = {
      'permission.grant': 'Granted Access',
      'permission.revoke': 'Revoked Access',
      'permission.bulk_update': 'Updated Permissions',
      'user.role_change': 'Changed User Role',
      'user.added': 'Added User',
      'user.removed': 'Removed User',
      'rollback.executed': 'Executed Rollback',
      'security.unauthorized_attempt': 'Unauthorized Attempt',
      'feature_flag.created': 'Created Feature Flag',
      'feature_flag.updated': 'Updated Feature Flag',
      'staff.tier_changed': 'Changed Staff Tier',
    };
    return labels[action] || action;
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return 'Unknown';

    // Handle different timestamp formats
    let date;
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      // Firestore timestamp
      date = timestamp.toDate();
    } else if (timestamp._seconds) {
      // Firestore timestamp object
      date = new Date(timestamp._seconds * 1000);
    } else if (typeof timestamp === 'string') {
      // ISO string
      date = new Date(timestamp);
    } else {
      date = new Date(timestamp);
    }

    return format(date, 'MMM d, yyyy h:mm a');
  };

  const filteredLogs = auditLogs?.filter(log => {
    if (!log) return false;
    if (filterUser && log.user?.email !== filterUser) return false;
    if (filterAction !== 'all' && !log.action?.includes(filterAction)) return false;
    if (filterTarget && log.target?.id && !log.target.id.toLowerCase().includes(filterTarget.toLowerCase())) return false;
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        log.user?.email?.toLowerCase().includes(searchLower) ||
        log.target?.displayName?.toLowerCase().includes(searchLower) ||
        (log.searchTags || []).some(tag => tag.toLowerCase().includes(searchLower))
      );
    }
    return true;
  });

  const handleExport = () => {
    if (!filteredLogs) return;

    const csv = [
      ['Timestamp', 'User', 'Action', 'Target', 'Changes'],
      ...filteredLogs.map(log => [
        formatTimestamp(log.timestamp),
        log.user?.email || 'Unknown',
        getActionLabel(log.action || ''),
        log.target?.displayName || 'N/A',
        JSON.stringify(log.changes?.diff || [])
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  if (isLoading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Audit Log</Typography>
        <Typography>Loading audit entries...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Audit Log</Typography>
        <Alert severity="error">Failed to load audit log</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <HistoryIcon color="primary" sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4">Audit Log</Typography>
            <Typography variant="body2" color="text.secondary">
              Complete history of all changes
            </Typography>
          </Box>
        </Box>
        <Button
          startIcon={<DownloadIcon />}
          variant="outlined"
          onClick={handleExport}
        >
          Export CSV
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Filter by User"
                variant="outlined"
                size="small"
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Action Type</InputLabel>
                <Select
                  value={filterAction}
                  label="Action Type"
                  onChange={(e) => setFilterAction(e.target.value)}
                >
                  <MenuItem value="all">All Actions</MenuItem>
                  <MenuItem value="permission">Permission Changes</MenuItem>
                  <MenuItem value="user">User Management</MenuItem>
                  <MenuItem value="rollback">Rollbacks</MenuItem>
                  <MenuItem value="security">Security Events</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Filter by Target"
                variant="outlined"
                size="small"
                value={filterTarget}
                onChange={(e) => setFilterTarget(e.target.value)}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Audit Log Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Timestamp</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Target</TableCell>
              <TableCell>Summary</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLogs?.map((log) => (
              <React.Fragment key={log.id}>
                <TableRow hover>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleExpandClick(log.id)}
                    >
                      {expandedRows[log.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatTimestamp(log.timestamp)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                        {log.user?.email?.[0]?.toUpperCase() || '?'}
                      </Avatar>
                      <Typography variant="body2">{log.user?.email || 'Unknown'}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getActionIcon(log.action)}
                      <Typography variant="body2">
                        {getActionLabel(log.action)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {log.target?.displayName ? (
                      <Chip
                        label={log.target.displayName}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">N/A</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {log.changes?.diff ? `${log.changes.diff.length} change${log.changes.diff.length !== 1 ? 's' : ''}` :
                       log.details ? 'Details available' : 'No changes'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {log.metadata?.rollbackToken &&
                     log.metadata?.rollbackExpiresAt?.toMillis() > Date.now() && (
                      <Tooltip title="Rollback this change">
                        <IconButton
                          size="small"
                          color="warning"
                          href={`/rollback/${log.metadata?.rollbackToken}`}
                        >
                          <UndoIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 0 }}>
                    <Collapse in={expandedRows[log.id]} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 3 }}>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom>
                              Change Details
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                              {log.changes?.diff ? log.changes.diff.map((diff, idx) => (
                                <Box key={idx} sx={{ mb: 1 }}>
                                  <Chip
                                    label={diff.operation}
                                    size="small"
                                    color={diff.operation === 'add' ? 'success' : 'error'}
                                    sx={{ mr: 1 }}
                                  />
                                  <Typography variant="body2" component="span">
                                    {diff.field}: {diff.newValue || diff.oldValue}
                                  </Typography>
                                </Box>
                              )) : log.details ? (
                                <Typography variant="body2">
                                  {JSON.stringify(log.details, null, 2)}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No details available
                                </Typography>
                              )}
                            </Paper>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom>
                              Metadata
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                              <Typography variant="body2">
                                <strong>IP Address:</strong> {log.user?.ip_address || 'N/A'}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Environment:</strong> {log.metadata?.environment || log.environment || 'N/A'}
                              </Typography>
                              {log.metadata?.changeRequestId && (
                                <Typography variant="body2">
                                  <strong>Change Request:</strong> {log.metadata?.changeRequestId}
                                </Typography>
                              )}
                              {log.searchTags && log.searchTags.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <strong>Tags:</strong>{' '}
                                  {log.searchTags.map(tag => (
                                    <Chip
                                      key={tag}
                                      label={tag}
                                      size="small"
                                      sx={{ ml: 0.5, mb: 0.5 }}
                                    />
                                  ))}
                                </Box>
                              )}
                            </Paper>
                          </Grid>
                        </Grid>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredLogs?.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No audit entries match your filters
          </Typography>
        </Box>
      )}
    </Box>
  );
}
